'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { BotIcon, ClockDotsIcon, ClockIcon, LoadingIcon } from '@insta-club/components';
import { Guides, Story } from '@insta-club/models';
import { useSessionStore } from '@insta-club/state';
import { t } from '@insta-club/translations';
import { Button, Checkbox, GuidesButton } from '@insta-club/ui';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import TaskItem from '../task-item';

interface Props {
    story: Story;
    programID: number;
    dayNumber: number;
    date: string;
    hasChatBot: boolean;
    canPerformTask: boolean;
    onGuide: (guide: Guides) => void;
}

const StoryProgram: React.FC<Props> = ({ story, date, hasChatBot, programID, dayNumber, canPerformTask, onGuide }) => {
    const settings = useSessionStore((state) => state.settings);
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState(story.is_done);
    const { trigger } = useSWRMutation(
        ApiKeys.POST_PROGRAM_TOGGLE_TASK(programID),
        (url, { arg }: { arg: { type: string; day_number: number } }) =>
            fetcher(url, 'dashboard', 'POST', { body: arg })
    );

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setLoading(true);
        setChecked(checked);
        try {
            await trigger({
                type: 'story',
                day_number: dayNumber
            });
            mutate(ApiKeys.GET_PROGRAM_SHOW(programID, dayNumber));
        } catch (err: unknown) {
            toast.removeAll();
            if (err instanceof Error) {
                toast.error('خطای ناشناخته رخ داده است');
            } else {
                const error = err as { status: number; message: string };
                if (error.status && error.status == 400) {
                    toast.error('وضعیت تسک های برنامه فقط در همان روز یا روز بعد قابل بروز رسانی می باشند.');
                }
            }
            setChecked(!checked);
        } finally {
            setLoading(false);
        }
    };

    const handelClickChat = () => {
        if (settings?.settings?.chat_bot_active) {
            router.push(`/assistant/chat-story/${date}`);
        } else {
            toast.error(t('service_temporarily_unavailable'));
        }
    };

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex items-center justify-between'>
                <p className='text-base font-semibold text-gray-600'>برنامه زمانی پیشنهادی</p>
                <GuidesButton guideKey='program_story' onClick={onGuide} noModal />
            </div>
            <p className='text-sm leading-6 font-medium text-gray-500'>
                {story.is_single && <>امروز فقط یک استوری جدید منتشر کن.</>}

                {!story.is_single && (
                    <>
                        این برنامه پیشنهادی با توجه به بهترین زمان پیک حضور مخاطبین در اینستاگرام است ، شما میتوانید با
                        توجه به شرایط پیجتون زمان ها رو تغییر بدید.
                    </>
                )}
            </p>
            <div className='custom-scrollbar flex max-h-96 flex-col divide-y divide-gray-200 overflow-y-auto rounded-xl bg-gray-50 px-2'>
                {story.started_at ? (
                    <TaskItem title={t('start_time')} value={story.started_at} icon={<ClockDotsIcon />} />
                ) : null}
                {story.finished_at ? (
                    <TaskItem title={t('end_time')} value={story.finished_at} icon={<ClockIcon />} />
                ) : null}
            </div>
            <div className='my-2 flex flex-col gap-2'>
                {hasChatBot ? (
                    <Button onClick={handelClickChat} variant='outline' size='sm' icon={<BotIcon />}>
                        {t('story_idea_with_intelligence')}
                    </Button>
                ) : null}
            </div>
            <div
                className={`mt-4 flex items-center justify-between ${loading ? 'pointer-events-none cursor-not-allowed opacity-60' : ''}`}
            >
                {canPerformTask ? (
                    <Checkbox
                        checked={checked}
                        onChange={handleChange}
                        disabled={loading}
                        label={<span className='text-base font-semibold text-gray-700'>{t('done_story')}</span>}
                    />
                ) : (
                    <span className='text-sm text-gray-500'>
                        وضعیت تسک‌های برنامه تنها در همان روز یا روز بعد قابل به‌روزرسانی است.
                    </span>
                )}
                {loading ? <LoadingIcon width={20} height={20} /> : null}
            </div>
        </div>
    );
};

export default StoryProgram;

